/**
 * Request Signing — HMAC-SHA256 signatures on edge function calls.
 * Prevents scripts/bots from calling edge functions directly.
 */
import { createLogger } from './logger';

const log = createLogger('REQUEST_SIGN');

// Client-side signing key — raises bar from "curl" to "reverse engineer binary"
// Real protection is server-side RLS + rate limiting
const CLIENT_SIGNING_KEY = 'duel_v1_signing_2026_production';

/**
 * Generate HMAC-SHA256 signature using Web Crypto API.
 */
async function hmacSha256(message: string, key: string): Promise<string> {
  try {
    const encoder = new TextEncoder();
    const keyData = encoder.encode(key);
    const messageData = encoder.encode(message);

    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign'],
    );

    const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData);
    const hashArray = Array.from(new Uint8Array(signature));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    // Fallback to simple hash if crypto unavailable (old devices)
    let hash = 0;
    for (let i = 0; i < message.length; i++) {
      hash = ((hash << 5) - hash) + message.charCodeAt(i);
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  }
}

export interface SignedRequest {
  signature: string;
  timestamp: number;
  nonce: string;
}

/**
 * Sign a request payload with HMAC-SHA256.
 */
export async function signRequest(userId: string, action: string): Promise<SignedRequest> {
  const timestamp = Date.now();
  const nonce = Math.random().toString(36).slice(2, 10);
  const payload = `${userId}:${action}:${timestamp}:${nonce}`;
  const signature = await hmacSha256(payload, CLIENT_SIGNING_KEY);
  return { signature, timestamp, nonce };
}

/**
 * Create signed headers for an edge function request.
 */
export async function getSignedHeaders(userId: string, action: string): Promise<Record<string, string>> {
  const { signature, timestamp, nonce } = await signRequest(userId, action);
  return {
    'x-duel-signature': signature,
    'x-duel-timestamp': String(timestamp),
    'x-duel-nonce': nonce,
    'x-duel-action': action,
  };
}
